import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { KENO_API, gameHeaders } from '../keno/fairnessApi';
import { formatApiErrorDetail } from '../../auth';
import { useChatRestriction } from './useChatRestriction';

export const useCommunityChat = (open, user) => {
  const owner = user?.id;
  const access = useChatRestriction(open, user);
  const latestOwner = useRef(owner); latestOwner.current = owner;
  const [messages, setMessages] = useState([]), [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(''), [error, setError] = useState(''), [sending, setSending] = useState(false);
  const pending = useRef(null), inFlight = useRef(false), epoch = useRef(0);
  const boundary = useRef(null);
  const [clearedBefore, setClearedBefore] = useState(null);
  const applyClear = useCallback((cutoff) => {
    if (!cutoff || (boundary.current && boundary.current >= cutoff)) return;
    boundary.current = cutoff; setClearedBefore(cutoff); epoch.current += 1;
    setMessages((old) => old.filter((message) => message.created_at > cutoff));
    window.dispatchEvent(new CustomEvent('gv-chat-cleared', { detail: cutoff }));
  }, []);
  useEffect(() => {
    const abort = new AbortController();
    epoch.current += 1;
    if (!open) return;
    setLoading(true);
    let polling = false;
    const load = async () => {
      if (polling) return;
      polling = true;
      const current = epoch.current;
      try {
        const { data } = await axios.get(`${KENO_API}/chat/messages`, { signal: abort.signal, headers: gameHeaders(!owner) });
        if (!abort.signal.aborted && current === epoch.current) {
          applyClear(data.cleared_before);
          const visible = data.messages.filter((message) => !boundary.current || message.created_at > boundary.current);
          setMessages((previous) => JSON.stringify(previous) === JSON.stringify(visible) ? previous : visible);
          setLoadError('');
        }
      } catch (failure) { if (!axios.isCancel(failure)) setLoadError('Chat is reconnecting…'); }
      finally { polling = false; if (!abort.signal.aborted) setLoading(false); }
    };
    load(); const timer = setInterval(load, 2500);
    return () => { abort.abort(); clearInterval(timer); epoch.current += 1; };
  }, [open, owner, applyClear]);
  useEffect(() => { pending.current = null; setError(''); }, [user?.id]);
  const send = async (message) => {
    const text = message.trim();
    if (!user || !text || inFlight.current || access.restriction) return false;
    if (pending.current?.message !== text) pending.current = { request_id: crypto.randomUUID(), message: text };
    inFlight.current = true; setSending(true); setError('');
    try {
      const { data } = await axios.post(`${KENO_API}/chat/messages`, pending.current, { headers: gameHeaders(false) });
      if (latestOwner.current !== owner) return false;
      epoch.current += 1;
      setMessages((old) => [...old.filter((entry) => entry.id !== data.id), data].sort((a, b) => a.created_at.localeCompare(b.created_at)).slice(-50));
      pending.current = null; return true;
    } catch (failure) {
      if (latestOwner.current === owner) {
        const detail = failure.response?.data?.detail;
        if (!access.acceptRestrictionError(detail)) setError(formatApiErrorDetail(detail || 'Message not sent. Please try again.'));
      }
      return false;
    }
    finally { inFlight.current = false; setSending(false); }
  };
  const remove = useCallback(async (id) => {
    try { await axios.delete(`${KENO_API}/chat/messages/${id}`, { headers: gameHeaders(false) }); epoch.current += 1; setMessages((old) => old.filter((entry) => entry.id !== id)); window.dispatchEvent(new CustomEvent('gv-chat-message-deleted', { detail: id })); setError(''); return true; }
    catch (failure) { setError(formatApiErrorDetail(failure.response?.data?.detail || 'Unable to remove message.')); return false; }
  }, []);
  return { messages, loading, loadError, error, sending, send, remove, restriction: access.restriction, clearedBefore, applyClear };
};